import axios from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { getCard, saveStatistic, sendInAppEvent } from '@/api/api'
import { FeedPostNotification } from '@/feed/Feed.interface'
import FeedPostFront from '@/components/FeedPostFront'
import Loading from '@/components/common/Loading'
import CardNotFound from '@/components/error/NotFound'
import FailedToFetch from '@/components/error/FailedToFetch'
import BrandedToolbar, {
  isToolbarHidden
} from '@/components/Toolbar/BrandedToolbar'
import useBranding from '@/lib/useBranding'
import usePageParams from '@/lib/usePageParams'

export default function PostFrontRoute() {
  const { id = '' } = useParams<{ id: string }>()
  const pageParams = usePageParams()
  const { branding, isLoading: isBrandingLoading } = useBranding(pageParams)

  const [feedPost, setFeedPost] = useState<FeedPostNotification | null>(null)
  const [isCardLoading, setIsCardLoading] = useState(true)
  const [isNotFound, setIsNotFound] = useState(false)
  const [hasServerError, setHasServerError] = useState(false)

  // The impression used to be recorded server-side, once per request. Keyed by
  // card id so a genuine navigation still counts, while React re-running the
  // effect (StrictMode, dependency churn) does not double-count.
  const impressionSentForRef = useRef<string | null>(null)

  const { alias, sdkAppId, sdkAppKey } = pageParams
  const key = `${sdkAppId}${sdkAppKey}`

  useEffect(() => {
    let cancelled = false
    setIsCardLoading(true)
    setIsNotFound(false)
    setHasServerError(false)

    // Deliberately isolated from the card fetch: a failing analytics call must
    // never turn a card that loaded fine into an error page.
    const sendImpression = async () => {
      if (impressionSentForRef.current === id) return
      impressionSentForRef.current = id

      try {
        await saveStatistic({
          key,
          data: {
            campaignGuid: id,
            alias,
            key: 'card_front_impression'
          },
          additionalHeaders: {
            source: 'native'
          }
        })
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    }

    const loadCard = async () => {
      try {
        const response = await getCard({
          params: {
            guid: id,
            alias
          },
          key,
          additionalHeaders: {
            source: 'native'
          }
        })

        if (!cancelled) {
          setFeedPost(response.data)
        }

        sendImpression()
      } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          if (!cancelled) setIsNotFound(true)
        } else {
          // eslint-disable-next-line no-console
          console.error(error)
          if (!cancelled) setHasServerError(true)
        }
      } finally {
        if (!cancelled) {
          setIsCardLoading(false)
        }
      }
    }

    loadCard()

    return () => {
      cancelled = true
    }
  }, [id, alias, key])

  const sendClickStat = useCallback(
    async ({ buttonNumber }: { buttonNumber: number }): Promise<void> => {
      try {
        await saveStatistic({
          key,
          data: {
            alias,
            campaignGuid: id,
            key:
              buttonNumber === 1
                ? 'card_front_button_click_one'
                : 'card_front_button_click_two'
          }
        })
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
    [id, alias, key]
  )

  const onSendInAppEvent = useCallback(
    async (inAppEvents: { name: string; occurredAt: string }[]) => {
      try {
        await sendInAppEvent({
          data: {
            alias,
            inAppEvents
          },
          key
        })
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
    [alias, key]
  )

  if (isBrandingLoading || isCardLoading) {
    return <Loading />
  }

  const hideToolbar = isToolbarHidden(branding)

  if (isNotFound) {
    return (
      <>
        <BrandedToolbar branding={branding} />
        <div className={`${hideToolbar ? 'pt-0' : 'pt-[55px]'}`}>
          <CardNotFound
            withBackButton={
              branding?.enabled && !branding?.feedHideGoBackToMainButton
            }
          />
        </div>
      </>
    )
  }

  if (hasServerError || !feedPost) {
    return (
      <>
        <BrandedToolbar branding={branding} />
        <div className={`${hideToolbar ? 'pt-0' : 'pt-[55px]'}`}>
          <FailedToFetch text="Failed to load feed post" />
        </div>
      </>
    )
  }

  return (
    <>
      <BrandedToolbar branding={branding} />
      <div className={`${hideToolbar ? 'pt-6' : 'pt-[75px]'}`}>
        <FeedPostFront
          lastInteractionAt={feedPost.lastInteractionAt}
          sendClickStat={sendClickStat}
          sendInAppEvent={onSendInAppEvent}
          feedPostFront={feedPost.front}
          campaignGuid={feedPost.campaignGuid}
        />
      </div>
    </>
  )
}
